import siteSettingsQuery from '~/sanity/queries/site-settings'
import siteMenusQuery from '~/sanity/queries/site-menus'
import formatMessage from '~/util/text/formatMessage'
import storage from '~/util/storage'

export const state = () => ({
  settings: null,
  loginLink: null,
  primaryMenu: {},
  secondaryMenu: {},
  legalPageMenu: [],
  googleClientId: undefined,
  googleSessionId: undefined,
  discountCode: undefined,
  featureFlag: undefined,
  refersionCode: undefined,
  banners: { active: [], current: null }
})

export const actions = {
  /** @param {import('vuex').ActionContext} */
  async init({ dispatch, commit, state }) {
    const { banners, ...settings } = await dispatch('fetchSiteSettings')
    const menus = await dispatch('fetchSiteMenus')
    commit('setSiteSettings', settings)
    commit('setSiteMenus', menus)
    commit('setSiteBanners', banners)
  },
  fetchSiteSettings() {
    return this.$sanity.fetch(siteSettingsQuery, {
      excludeCBD: process.env.nonCBDSite
    })
  },
  fetchSiteMenus() {
    return this.$sanity.fetch(siteMenusQuery)
  },
  async nuxtServerInit({ dispatch }, { env, $sentry }) {
    if (env.previewMode && process.server) {
      return
    }
    try {
      await dispatch('init')
      if (env.shopify) {
        await dispatch('shop/init')
      }
    } catch (e) {
      $sentry.captureException(e)
      // Fail on build
      throw e
    }
  },
  nuxtClientInit({ dispatch }, context) {
    dispatch('shop/nuxtClientInit')
  },
  getImageUrl(ctx, { image, width = 1024, height = 1024, crop = false }) {
    if (!image) {
      return
    }
    let sanityImage
    try {
      sanityImage = this.$sanityImage(image).auto('format')
      if (crop) {
        sanityImage = sanityImage
          .ignoreImageParams()
          .size(width, height)
          .fit('min')
      } else {
        sanityImage = sanityImage
          .ignoreImageParams()
          .size(width, height)
          .fit('max')
      }
    } catch (e) {
      this.$sentry.captureException(e)
    }
    return sanityImage ? sanityImage.url() : undefined
  }
}

export const mutations = {
  setSiteBanners(state, banners) {
    if (Array.isArray(banners?.active)) {
      state.banners = banners
    }

    let current = state.banners.active.find(banner =>
      Object.entries(banner.visibility).every(([key, value]) => {
        if (value) {
          if (key === 'discount') {
            return !!state.discountCode
          }
          if (key === 'devotion') {
            return storage.getItem('utm')?.utm_source === 'devotion'
          }
        }
        return true
      })
    )

    if (current) {
      current = {
        ...current,
        contents: current.contents?.map(item => ({
          ...item,
          text: formatMessage(item.text, { code: state.discountCode })
        }))
      }
    }

    state.banners.current = current || null
  },
  setSiteSettings(state, settings) {
    state.settings = settings
  },
  setSiteMenus(state, menus = {}) {
    menus = Object.entries(menus)
    for (const [key, val] of menus) {
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        state[key] = val
      }
    }
  },
  setGoogleClientId(state, id) {
    state.googleClientId = id || null
  },
  setGoogleSessionId(state, id) {
    state.googleSessionId = id || null
  },
  setDiscountCode(state, code) {
    state.discountCode = code || null
  },
  setFeatureFlag(state, flag) {
    state.featureFlag = flag || null
  },
  setRefersionCode(state, code) {
    state.refersionCode = code || null
  }
}
